import * as t from "io-ts";
import { RuleGroupV } from "@/types/conditions";
import { getIoTsStringsUnionFromConstStringArray } from "@/util/type-assertions";
import { TreeNode, TreeNodeDataV } from "@/types/tree";

export const wpMetaQueryCompare = [
  "=",
  "!=",
  ">",
  ">=",
  "<",
  "<=",
  "LIKE",
  "NOT LIKE",
  "IN",
  "NOT IN",
  "BETWEEN",
  "NOT BETWEEN",
  "EXISTS",
  "NOT EXISTS",
  "REGEXP",
  "NOT REGEXP",
  "RLIKE"
] as const;

export const wpMetaQueryType = [
  "NUMERIC",
  "BINARY",
  "CHAR",
  "DATE",
  "DATETIME",
  "DECIMAL",
  "SIGNED",
  "TIME",
  "UNSIGNED"
] as const;

const WpMetaQueryCompareV = getIoTsStringsUnionFromConstStringArray(
  wpMetaQueryCompare
);

export type WpMetaQueryCompare = t.TypeOf<typeof WpMetaQueryCompareV>;

const WpMetaQueryTypeV = getIoTsStringsUnionFromConstStringArray(
  wpMetaQueryType
);

export type WpMetaQueryType = t.TypeOf<typeof WpMetaQueryTypeV>;

const WpMetaQueryDataV = t.type({
  key: t.string,
  value: t.union([t.string, t.array(t.string)]),
  compare: WpMetaQueryCompareV,
  type: WpMetaQueryTypeV
});

const WpMetaQueryV: t.Type<WpMetaQuery> = t.type({
  relation: t.union([t.literal("AND"), t.literal("OR")]),
  metaQueries: t.array(
    t.union([WpMetaQueryDataV, t.recursion("WpMetaQuery", () => WpMetaQueryV)])
  )
});

export type WpMetaQueryData = t.TypeOf<typeof WpMetaQueryDataV>;

// Recursive since you can have AND|OR inside other AND|OR
// E.g: https://developer.wordpress.org/reference/classes/wp_meta_query/#comment-1989
export type WpMetaQuery = {
  relation: "AND" | "OR";
  metaQueries: (WpMetaQueryData | WpMetaQuery)[];
};

export const WpQueryV = t.type({
  custom: t.intersection([
    t.union([
      t.type({
        source: t.literal("post_types"),
        postTypes: t.array(t.string),
        conditions: t.union([t.array(RuleGroupV), t.undefined]),
        metaQuery: t.union([WpMetaQueryV, t.undefined])
      }),
      t.type({
        source: t.literal("related"),
        includeByTaxonomies: t.array(t.string),
        includeByAuthor: t.boolean
      }),
      t.type({
        source: t.literal("acf_relationship"),
        acfField: t.string
      })
    ]),
    t.type({
      orderBy: t.union([
        t.literal("date"),
        t.literal("modified"),
        t.literal("title"),
        t.literal("comment_count"),
        t.literal("menu_order"),
        t.literal("rand"),
        t.literal("acf_field"),
        t.literal("metabox_field")
      ]),
      offset: t.union([t.null, t.number]),
      postsPerPage: t.union([t.null, t.number]),
      totalPosts: t.union([t.null, t.number]),
      order: t.union([t.literal("ASC"), t.literal("DESC")]),
      date: t.union([
        t.literal("all"),
        t.literal("-1 day"),
        t.literal("-1 week"),
        t.literal("-1 month"),
        t.literal("-3 months"),
        t.literal("-1 year"),
        t.literal("custom")
      ]),
      beforeDate: t.union([t.null, t.string]),
      afterDate: t.union([t.null, t.string]),
      ignoreStickyPosts: t.boolean,
      ignoreCurrentPost: t.boolean,
      acfField: t.union([t.null, t.string]),
      metaboxField: t.union([t.null, t.string])
    })
  ]),
  text: t.string,
  php: t.string,
  active: t.union([
    t.literal(""),
    t.literal("custom"),
    t.literal("text"),
    t.literal("php")
  ])
});

export type WpQuery = t.TypeOf<typeof WpQueryV>;
